/**
 * `fabric.CraftaraVideo`: a video clip as a canvas layer.
 *
 * A registered fabric.Image subclass rather than an Image with ad-hoc props,
 * because serialization is the hard part and only a subclass controls
 * toObject / fromObject.
 *
 * REGISTRATION: registerVideoObject() must run before EVERY loadFromJSON that
 * could meet a video layer, or Fabric drops the object. Calling it from
 * lib/fabric.ts would create an import cycle (this module imports the shim),
 * so it is an explicit, idempotent call at each site: the live editor's
 * initial load and loadCanvasDataIntoFabric, and lib/offscreenCanvas.ts
 * (which covers renderPage, page export and the video scene compositor).
 */

import fabric from '@/lib/fabric';
import type { fabric as FabricNS } from 'fabric';
import type { VideoClipRef } from '@canvas/shared-types';
import { resolveMediaBlob } from './videoAssetUpload';
import { serializableClipRef, serializablePosterSrc, VIDEO_OBJECT_TYPE } from './videoClip';

export { VIDEO_OBJECT_TYPE } from './videoClip';

/** Where the layer's media stands in this tab; never serialized. */
export type VideoMediaStatus = 'poster' | 'ready' | 'missing';

export interface CraftaraVideoObject extends FabricNS.Image {
  type: 'craftaraVideo';
  craftaraVideo: VideoClipRef;
  /** Live decode source. null until attached. */
  _videoEl: HTMLVideoElement | null;
  /** The poster (or placeholder) drawn whenever no video is attached. */
  _posterEl: HTMLImageElement | HTMLCanvasElement;
  /** Durable poster URL for serialization; never blob: or data:. */
  _posterUrl: string;
  _mediaStatus: VideoMediaStatus;
  attachElement(el: HTMLVideoElement): void;
  detachElement(): void;
}

type VideoClass = {
  new (element: HTMLImageElement | HTMLCanvasElement, options?: Record<string, unknown>): CraftaraVideoObject;
  fromObject(object: Record<string, unknown>, callback: (obj: CraftaraVideoObject) => void): void;
};

let registered = false;

function videoClass(): VideoClass {
  registerVideoObject();
  return (fabric as unknown as { CraftaraVideo: VideoClass }).CraftaraVideo;
}

/**
 * Fabric draws `_element` with a source rect of min(element.width, this.width).
 * A <video>'s `width` attribute defaults to 0, so without this it draws nothing.
 */
function sizeToIntrinsic(el: HTMLVideoElement): void {
  if (el.videoWidth && el.width !== el.videoWidth) el.width = el.videoWidth;
  if (el.videoHeight && el.height !== el.videoHeight) el.height = el.videoHeight;
}

/** Stand-in when no poster can be loaded, so the layer still renders and never throws. */
function placeholderPoster(clip: VideoClipRef | undefined): HTMLCanvasElement {
  const w = Math.max(16, clip?.naturalWidth || 640);
  const h = Math.max(16, clip?.naturalHeight || 360);
  const scale = Math.min(1, 640 / Math.max(w, h));
  const c = document.createElement('canvas');
  c.width = Math.round(w * scale);
  c.height = Math.round(h * scale);
  const ctx = c.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#1f2430';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    const s = Math.min(c.width, c.height) * 0.18;
    ctx.beginPath();
    ctx.moveTo(c.width / 2 - s / 2, c.height / 2 - s * 0.6);
    ctx.lineTo(c.width / 2 + s * 0.6, c.height / 2);
    ctx.lineTo(c.width / 2 - s / 2, c.height / 2 + s * 0.6);
    ctx.closePath();
    ctx.fill();
  }
  return c;
}

function loadImage(url: string, crossOrigin: 'anonymous' | null): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    fabric.util.loadImage(
      url,
      (img: HTMLImageElement | null, isError?: boolean) => resolve(!img || isError ? null : img),
      null,
      crossOrigin ?? undefined
    );
  });
}

/**
 * Poster for a stored layer: the IDB blob first (offline, no CORS), then the
 * Storage copy, then the serialized download URL. Never throws — a missing
 * poster becomes a placeholder.
 */
async function resolvePoster(
  clip: VideoClipRef | undefined,
  src: string
): Promise<{ el: HTMLImageElement | HTMLCanvasElement; found: boolean }> {
  if (clip && (clip.posterIdbKey || clip.posterStoragePath)) {
    const blob = await resolveMediaBlob({ idbKey: clip.posterIdbKey, storagePath: clip.posterStoragePath });
    if (blob) {
      const url = URL.createObjectURL(blob);
      const img = await loadImage(url, null);
      // The decoded <img> stays drawable after revoke.
      URL.revokeObjectURL(url);
      if (img) return { el: img, found: true };
    }
  }
  if (src) {
    const img = await loadImage(src, 'anonymous');
    if (img) return { el: img, found: true };
  }
  return { el: placeholderPoster(clip), found: false };
}

/** Idempotent; module-flag guarded. See the file JSDoc for where it must be called. */
export function registerVideoObject(): void {
  if (registered) return;
  registered = true;
  const ns = fabric as unknown as Record<string, unknown>;
  if (ns.CraftaraVideo) return;

  const Klass = fabric.util.createClass(fabric.Image, {
    type: VIDEO_OBJECT_TYPE,
    // Fabric caches each object to an offscreen bitmap; with caching on a
    // playing video would freeze on its first frame forever.
    objectCaching: false,

    initialize(
      this: CraftaraVideoObject & { callSuper: (name: string, ...args: unknown[]) => unknown },
      element: HTMLImageElement | HTMLCanvasElement,
      options: Record<string, unknown> = {}
    ) {
      this.callSuper('initialize', element, options);
      this.objectCaching = false;
      this._videoEl = null;
      this._posterEl = element;
      this._posterUrl = '';
      this._mediaStatus = 'poster';
    },

    attachElement(this: CraftaraVideoObject, el: HTMLVideoElement) {
      sizeToIntrinsic(el);
      this._videoEl = el;
      (this as unknown as { _element: unknown })._element = el;
      this._mediaStatus = 'ready';
      this.dirty = true;
    },

    detachElement(this: CraftaraVideoObject) {
      this._videoEl = null;
      (this as unknown as { _element: unknown })._element = this._posterEl;
      if (this._mediaStatus === 'ready') this._mediaStatus = 'poster';
      this.dirty = true;
    },

    toObject(
      this: CraftaraVideoObject & { callSuper: (name: string, ...args: unknown[]) => Record<string, unknown> },
      propertiesToInclude: string[] = []
    ) {
      const json = this.callSuper('toObject', [...propertiesToInclude, 'craftaraVideo']);
      // canvasData carries a URL string plus the ~400-byte descriptor and
      // nothing else: getSrc() would return the attached blob: URL here.
      json.src = serializablePosterSrc(this._posterUrl);
      json.craftaraVideo = serializableClipRef(this.craftaraVideo);
      json.filters = [];
      return json;
    },
  }) as VideoClass;

  /*
   * Does NOT attach a <video>. Attaching is liveVideoLayers' job in the editor
   * and videoFrameSource's in export: opening a project with eight clips must
   * not spin up eight decoders.
   */
  Klass.fromObject = (object, callback) => {
    const clip = object.craftaraVideo as VideoClipRef | undefined;
    const src = serializablePosterSrc(object.src as string | undefined);
    void resolvePoster(clip, src).then(({ el, found }) => {
      const options: Record<string, unknown> = { ...object };
      delete options.src;
      delete options.filters;
      delete options.resizeFilter;
      const obj = new Klass(el, options);
      obj._posterUrl = src;
      if (!found) obj._mediaStatus = 'missing';
      callback(obj);
    });
  };

  ns.CraftaraVideo = Klass;
}

/** A new video layer over its poster frame; valid before any media loads. */
export function createVideoObject(
  clip: VideoClipRef,
  posterEl: HTMLImageElement | HTMLCanvasElement,
  posterUrl = ''
): CraftaraVideoObject {
  const Klass = videoClass();
  const obj = new Klass(posterEl, {
    width: clip.naturalWidth,
    height: clip.naturalHeight,
    craftaraVideo: clip,
  });
  obj._posterUrl = serializablePosterSrc(posterUrl);
  return obj;
}

/** Point an existing layer at re-imported media, keeping its transform and animation. */
export function replaceVideoMedia(
  obj: CraftaraVideoObject,
  clip: VideoClipRef,
  posterEl: HTMLImageElement | HTMLCanvasElement,
  posterUrl = ''
): void {
  obj.detachElement();
  obj._posterEl = posterEl;
  (obj as unknown as { _element: unknown })._element = posterEl;
  obj._posterUrl = serializablePosterSrc(posterUrl);
  obj._mediaStatus = 'poster';
  obj.set('craftaraVideo', clip);
}

export function isVideoObject(obj: FabricNS.Object | null | undefined): obj is CraftaraVideoObject {
  return Boolean(obj && obj.type === VIDEO_OBJECT_TYPE && (obj as Partial<CraftaraVideoObject>).craftaraVideo);
}

/** Every video layer on a canvas, including inside groups. */
export function videoObjectsOn(canvas: { getObjects(): FabricNS.Object[] }): CraftaraVideoObject[] {
  const out: CraftaraVideoObject[] = [];
  const walk = (objects: FabricNS.Object[]): void => {
    for (const obj of objects) {
      if (isVideoObject(obj)) out.push(obj);
      const children = (obj as FabricNS.Group)._objects;
      if (Array.isArray(children)) walk(children);
    }
  };
  walk(canvas.getObjects());
  return out;
}
